/**
 * First-touch ad-click/UTM attribution capture.
 * Reads marketing query params once on first landing and remembers them in
 * localStorage so they survive until signup, even if the user doesn't convert
 * on the same visit. Ad-click signals always take precedence over a user's
 * manual "how did you hear about us?" dropdown pick when both exist.
 */

import { LEAD_TRAFFIC_SOURCES } from "../config/leadCrmConfig";

const STORAGE_KEY = "nema_first_touch_attribution_v1";
const FIRST_TOUCH_TTL_DAYS = 90;

function safeLocalStorage() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function readQueryParams() {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search || "");
  const get = (key) => {
    const value = params.get(key);
    return value ? value.trim() : "";
  };
  return {
    utmSource: get("utm_source"),
    utmMedium: get("utm_medium"),
    utmCampaign: get("utm_campaign"),
    utmContent: get("utm_content"),
    utmTerm: get("utm_term"),
    fbclid: get("fbclid"),
    gclid: get("gclid"),
  };
}

function hasAnySignal(attribution) {
  if (!attribution) return false;
  return Boolean(
    attribution.utmSource ||
      attribution.utmMedium ||
      attribution.utmCampaign ||
      attribution.fbclid ||
      attribution.gclid
  );
}

function isExpired(record) {
  if (!record?.expiresAt) return true;
  const expiresAt = new Date(record.expiresAt).getTime();
  return !Number.isFinite(expiresAt) || expiresAt < Date.now();
}

/** Reads a non-expired first-touch record, clearing it (and returning null) if expired. */
export function getFirstTouchAttribution() {
  const storage = safeLocalStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw);
    if (isExpired(record)) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    return record;
  } catch {
    return null;
  }
}

/**
 * Captures UTM/fbclid/gclid on first landing. True first-touch: does not
 * overwrite an existing valid record, unless the existing record carries no
 * ad-click signal at all (a bare direct-visit placeholder) and this visit does.
 */
export function captureFirstTouchAttribution() {
  const storage = safeLocalStorage();
  if (!storage) return null;

  const incoming = readQueryParams();
  const existing = getFirstTouchAttribution();

  if (existing && (hasAnySignal(existing) || !hasAnySignal(incoming))) {
    return existing;
  }

  const now = new Date();
  const expiresAt = new Date(now.getTime() + FIRST_TOUCH_TTL_DAYS * 86400000);
  const record = {
    ...incoming,
    landingUrl: typeof window !== "undefined" ? window.location.href : "",
    referrer: typeof document !== "undefined" ? document.referrer || null : null,
    capturedAt: now.toISOString(),
    expiresAt: expiresAt.toISOString(),
  };

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* storage unavailable/full — attribution just won't persist */
  }
  return record;
}

export function clearFirstTouchAttribution() {
  const storage = safeLocalStorage();
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Maps a captured attribution record to a known lead-source id + channel, or null if no match. */
export function mapAttributionToLeadSource(attribution) {
  if (!attribution) return null;

  if (attribution.fbclid) {
    const match = LEAD_TRAFFIC_SOURCES.find((s) => s.id === "FACEBOOK_ADS");
    if (match) return { leadSourceId: match.id, leadSourceChannel: match.channel };
  }
  if (attribution.gclid) {
    const match = LEAD_TRAFFIC_SOURCES.find((s) => s.id === "GOOGLE_ADS");
    if (match) return { leadSourceId: match.id, leadSourceChannel: match.channel };
  }

  const utmSource = String(attribution.utmSource || "").toLowerCase();
  if (!utmSource) return null;

  if (utmSource.includes("facebook") || utmSource.includes("meta")) {
    const match = LEAD_TRAFFIC_SOURCES.find((s) => s.id === "FACEBOOK_ADS");
    if (match) return { leadSourceId: match.id, leadSourceChannel: match.channel };
  }
  if (utmSource.includes("instagram")) {
    const match = LEAD_TRAFFIC_SOURCES.find((s) => s.id === "INSTAGRAM");
    if (match) return { leadSourceId: match.id, leadSourceChannel: match.channel };
  }
  if (utmSource.includes("google")) {
    const isOrganic = String(attribution.utmMedium || "").toLowerCase() === "organic";
    const match = LEAD_TRAFFIC_SOURCES.find((s) => s.id === (isOrganic ? "ORGANIC_SEARCH" : "GOOGLE_ADS"));
    if (match) return { leadSourceId: match.id, leadSourceChannel: match.channel };
  }
  if (utmSource.includes("whatsapp")) {
    const match = LEAD_TRAFFIC_SOURCES.find((s) => s.id === "WHATSAPP");
    if (match) return { leadSourceId: match.id, leadSourceChannel: match.channel };
  }

  return null;
}
